import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import {
  TicketCostBillingLocationTransportValidator,
  TicketCostDetailsRequest,
  TicketCostDetailsTransport,
  TicketCostOcrmArrayTransportValidator,
  TicketCostPreviewResponseValidator,
} from '../../model/ticket-cost';
import type { Decoder } from 'io-ts';
import { otsBaseApi } from '../ots-api';

const customError = (error: unknown): FetchBaseQueryError => ({
  status: 'CUSTOM_ERROR',
  error: error instanceof Error ? error.message : String(error),
});

/** Log failures without response bodies, credentials or financial payloads. */
export function reportTicketCostLoadError(phase: string, error: unknown) {
  const details = error && typeof error === 'object' ? error : {};
  console.warn(`Unable to load ticket cost ${phase}.`, {
    status: 'status' in details ? details.status : 'QUERY_ERROR',
    originalStatus:
      'originalStatus' in details ? details.originalStatus : undefined,
    message:
      'status' in details &&
      details.status === 'CUSTOM_ERROR' &&
      'error' in details
        ? details.error
        : undefined,
  });
}

/** Report field paths, never raw ticket data, when a successful response is incompatible. */
function readCostResponse<T>(
  validator: Decoder<unknown, T>,
  value: unknown,
  responseName: string
): T {
  const decoded = validator.decode(value);
  if (decoded._tag === 'Right') return decoded.right;
  const fields = [
    ...new Set(
      decoded.left.map((error) =>
        error.context
          .map((entry) => entry.key)
          .filter(Boolean)
          .join('.')
      )
    ),
  ];
  throw new Error(
    `${responseName} contains incompatible fields: ${
      fields.join(', ') || 'response'
    }`
  );
}

type TicketCostBaseQuery = (
  args: FetchArgs
) => ReturnType<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>>;

/** Load cost versions and billing explanations independently so either can remain useful. */
export async function loadTicketCostDetails(
  { ticketId, database }: TicketCostDetailsRequest,
  baseQuery: TicketCostBaseQuery
) {
  try {
    const savedResult = await baseQuery({
      url: `/ocrms/object/TRANSPORT_TRANSPORT_ASSOC/${encodeURIComponent(
        ticketId
      )}/versions`,
      method: 'GET',
      params: database ? { tenantCode: database } : undefined,
    });
    if (savedResult.error) {
      reportTicketCostLoadError('versions', savedResult.error);
      return { error: savedResult.error };
    }

    const saved = readCostResponse(
      TicketCostOcrmArrayTransportValidator,
      savedResult.data,
      'Saved cost response'
    );
    let preview: TicketCostDetailsTransport['preview'] = [];
    let previewError: string | undefined;

    const previewResult = await baseQuery({
      url: `/reports/ttacost/${encodeURIComponent(ticketId)}`,
      method: 'POST',
      params: { ...(database ? { database } : {}), preview: true },
    });

    if (previewResult.error) {
      reportTicketCostLoadError('preview', previewResult.error);
      previewError = 'Preview calculation is unavailable.';
    } else {
      try {
        const response = readCostResponse(
          TicketCostPreviewResponseValidator,
          previewResult.data,
          'Preview cost response'
        );
        preview =
          response.activityList
            ?.map((activity) => activity.transportTransportAssoc)
            .find((ticket) => ticket?.id === ticketId)
            ?.objectCostRateModelList ?? [];
      } catch (error) {
        console.warn('Unable to read the preview cost response.', error);
        previewError = 'The preview response could not be read.';
      }
    }

    let billingLocation: TicketCostDetailsTransport['billingLocation'];
    let billingLocationError: string | undefined;
    const persistedCostId = saved.find((cost) => cost.id)?.id;
    const billingResult = await baseQuery({
      url: persistedCostId
        ? `/ocrms/${encodeURIComponent(persistedCostId)}/billing-location`
        : `/ttas/${encodeURIComponent(ticketId)}/billing-location`,
      method: 'GET',
      params: {
        ...(database ? { tenantCode: database } : {}),
        ...(!persistedCostId && preview[0]?.rateModelId
          ? { rateModelId: preview[0].rateModelId }
          : {}),
      },
    });
    if (billingResult.error) {
      reportTicketCostLoadError('billing location', billingResult.error);
      billingLocationError = 'Billing location details could not be loaded.';
    } else {
      try {
        billingLocation = readCostResponse(
          TicketCostBillingLocationTransportValidator,
          billingResult.data,
          'Billing location response'
        );
      } catch (error) {
        console.warn('Unable to read the billing location response.', error);
        billingLocationError = 'Billing location details could not be loaded.';
      }
    }

    return {
      data: {
        saved,
        preview,
        billingLocation,
        previewError,
        billingLocationError,
      },
    };
  } catch (error) {
    console.warn('Unable to load ticket cost versions.', error);
    return { error: customError(error) };
  }
}

export const ticketCostApi = otsBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTicketCostDetails: builder.query<
      TicketCostDetailsTransport,
      TicketCostDetailsRequest
    >({
      async queryFn({ ticketId, database }, _api, _extraOptions, baseQuery) {
        return loadTicketCostDetails({ ticketId, database }, baseQuery);
      },
    }),
  }),
});

export const { useGetTicketCostDetailsQuery } = ticketCostApi;
